import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSites, createSite, updateSiteApi, deleteSiteApi } from "@/lib/sites";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  Users,
  Shield,
  Key,
  PencilSimple,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function UsersPage() {
  const { sites: sitesList, refresh: refreshSites } = useSites({ includeInactive: true });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "user",
    allowed_sites: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", password: "", name: "", role: "user", allowed_sites: [] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    // Migrate legacy `site` string to `allowed_sites` list for editing
    let initSites = Array.isArray(u.allowed_sites) ? [...u.allowed_sites] : [];
    if (initSites.length === 0 && u.site && u.site !== "ALL") {
      initSites = [u.site];
    }
    setForm({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      allowed_sites: initSites,
    });
    setOpen(true);
  };

  const toggleSite = (code) => {
    setForm((f) => {
      const has = f.allowed_sites.includes(code);
      return {
        ...f,
        allowed_sites: has
          ? f.allowed_sites.filter((s) => s !== code)
          : [...f.allowed_sites, code],
      };
    });
  };

  const submit = async () => {
    try {
      // Payload always uses allowed_sites (multi-site). Keep legacy `site` for compat.
      const legacySite = form.allowed_sites.length === 1 ? form.allowed_sites[0] : "ALL";
      if (editing) {
        await api.patch(`/users/${editing.id}`, {
          name: form.name,
          role: form.role,
          allowed_sites: form.allowed_sites,
          site: legacySite,
        });
        toast.success("Utilisateur mis à jour");
      } else {
        if (form.password.length < 6) {
          toast.error("Mot de passe trop court (min 6)");
          return;
        }
        if (form.role !== "admin" && form.allowed_sites.length === 0) {
          toast.error("Sélectionnez au moins un site (ou rôle Admin)");
          return;
        }
        await api.post("/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          allowed_sites: form.allowed_sites,
          site: legacySite,
        });
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const removeUser = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Utilisateur supprimé");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const submitPassword = async () => {
    if (!pwTarget) return;
    if (pwValue.length < 6) {
      toast.error("Mot de passe trop court");
      return;
    }
    try {
      await api.patch(`/users/${pwTarget.id}/password`, { password: pwValue });
      toast.success("Mot de passe mis à jour");
      setPwTarget(null);
      setPwValue("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-8" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline"
        >
          <ArrowLeft size={16} weight="bold" /> Retour
        </Link>
      </div>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tag">Gestion des accès</div>
          <h1
            className="text-4xl md:text-5xl font-black uppercase tracking-tighter mt-2"
            style={{ fontFamily: "Chivo" }}
          >
            Utilisateurs
          </h1>
          <p className="text-zinc-600 mt-2 max-w-xl">
            Seuls les administrateurs peuvent créer ou supprimer des comptes.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="bg-zinc-900 text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-zinc-700 inline-flex items-center gap-2 brutalist-shadow-sm"
          data-testid="add-user-btn"
        >
          <Plus size={16} weight="bold" /> Nouvel utilisateur
        </button>
      </div>

      <div className="border border-zinc-900 bg-zinc-100" data-testid="users-list">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-900 text-white">
          <div className="col-span-4 label-tag !text-zinc-400">Email</div>
          <div className="col-span-3 label-tag !text-zinc-400">Nom</div>
          <div className="col-span-2 label-tag !text-zinc-400">Rôle</div>
          <div className="col-span-2 label-tag !text-zinc-400">Sites</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-6 text-center">Chargement…</div>}
        {!loading &&
          users.map((u) => {
            const sitesForUser =
              u.role === "admin"
                ? ["Tous les sites"]
                : Array.isArray(u.allowed_sites) && u.allowed_sites.length > 0
                  ? u.allowed_sites
                  : u.site && u.site !== "ALL"
                    ? [u.site]
                    : ["Tous les sites"];
            return (
            <div
              key={u.id}
              className="grid grid-cols-12 gap-3 px-4 py-4 bg-white border-b border-zinc-200 items-center"
              data-testid={`user-row-${u.email}`}
            >
              <div className="col-span-4 mono text-sm font-bold">{u.email}</div>
              <div className="col-span-3 text-sm">{u.name || "—"}</div>
              <div className="col-span-2">
                <span
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${
                    u.role === "admin"
                      ? "bg-zinc-900 text-white"
                      : "bg-zinc-100 border border-zinc-300"
                  }`}
                >
                  {u.role === "admin" && <Shield size={10} weight="bold" />}
                  {u.role}
                </span>
              </div>
              <div className="col-span-2 mono text-xs flex flex-wrap gap-1">
                {sitesForUser.map((s) => (
                  <span
                    key={s}
                    className={`px-1.5 py-0.5 text-[10px] font-bold border ${
                      s === "Tous les sites"
                        ? "border-[var(--ny-gold)] bg-[var(--ny-gold)] text-zinc-900"
                        : "border-zinc-300 bg-zinc-50 text-zinc-700"
                    }`}
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button
                  onClick={() => openEdit(u)}
                  className="p-1 hover:bg-zinc-100"
                  data-testid={`edit-user-${u.email}`}
                >
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button
                  onClick={() => {
                    setPwTarget(u);
                    setPwValue("");
                  }}
                  className="p-1 hover:bg-zinc-100"
                  data-testid={`password-user-${u.email}`}
                  title="Changer mot de passe"
                >
                  <Key size={14} weight="bold" />
                </button>
                <button
                  onClick={() => setConfirmDelete(u)}
                  className="p-1 hover:bg-red-100"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash size={14} weight="bold" className="text-red-600" />
                </button>
              </div>
            </div>
            );
          })}
      </div>

      {/* ---- Gestion des sites ---- */}
      <SitesManager sites={sitesList} onRefresh={refreshSites} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-black uppercase tracking-tight"
              style={{ fontFamily: "Chivo" }}
            >
              {editing ? "Modifier utilisateur" : "Nouvel utilisateur"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {editing
                ? "Mettez à jour les infos du compte."
                : "Créez un compte avec un mot de passe temporaire."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Email</Label>
              <Input
                type="email"
                disabled={!!editing}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@flotte.fr"
                className="rounded-none border-zinc-900 mono"
                data-testid="user-input-email"
              />
            </div>
            {!editing && (
              <div>
                <Label className="label-tag">Mot de passe initial *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="rounded-none border-zinc-900 mono"
                  data-testid="user-input-password"
                />
              </div>
            )}
            <div>
              <Label className="label-tag">Nom</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none border-zinc-900"
                placeholder="Jean Dupont"
                data-testid="user-input-name"
              />
            </div>
            <div>
              <Label className="label-tag">Rôle</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger className="rounded-none border-zinc-900" data-testid="user-input-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="user">Utilisateur</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                {form.role === "admin"
                  ? "Cochez des sites pour restreindre votre propre vue (laisser vide = tous les sites). Les droits admin restent inchangés."
                  : "Cochez les sites auxquels cet utilisateur a accès."}
              </p>
            </div>
            <div>
              <Label className="label-tag">
                Sites autorisés {form.role === "admin" ? "" : "*"}
              </Label>
              <div className="border border-zinc-900 divide-y divide-zinc-200" data-testid="user-input-allowed-sites">
                {sitesList.filter((s) => s.active !== false).length === 0 && (
                  <div className="p-3 text-xs text-zinc-400 uppercase tracking-widest">
                    Aucun site configuré
                  </div>
                )}
                {sitesList
                  .filter((s) => s.active !== false)
                  .map((s) => {
                    const checked = form.allowed_sites.includes(s.code);
                    return (
                      <label
                        key={s.code}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 ${checked ? "bg-[var(--ny-gold)]/15" : ""}`}
                        data-testid={`site-checkbox-${s.code}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSite(s.code)}
                          className="w-4 h-4 accent-zinc-900"
                        />
                        <span className="mono text-sm font-bold">{s.code}</span>
                        {s.name && <span className="text-xs text-zinc-500">— {s.name}</span>}
                      </label>
                    );
                  })}
              </div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                {form.role === "admin"
                  ? form.allowed_sites.length === 0
                    ? "Aucune restriction → tous les sites visibles"
                    : `Vue restreinte à ${form.allowed_sites.length} site(s)`
                  : form.allowed_sites.length === 0
                    ? "⚠️ Sans site coché, cet utilisateur ne verra AUCUNE donnée."
                    : `${form.allowed_sites.length} site(s) coché(s)`}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={submit}
              className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700"
              data-testid="user-save-btn"
            >
              {editing ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md">
          <DialogHeader>
            <DialogTitle
              className="text-xl font-black uppercase tracking-tight"
              style={{ fontFamily: "Chivo" }}
            >
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {pwTarget?.email}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tag">Nouveau mot de passe</Label>
            <Input
              type="password"
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              className="rounded-none border-zinc-900 mono"
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPwTarget(null)}
              className="rounded-none border-zinc-900"
            >
              Annuler
            </Button>
            <Button
              onClick={submitPassword}
              className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700"
              data-testid="confirm-password-btn"
            >
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-none border border-zinc-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.email} perdra immédiatement l&apos;accès à l&apos;application.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-900">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={removeUser}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SitesManager({ sites, onRefresh }) {
  const [form, setForm] = useState({ code: "", name: "" });
  const [editing, setEditing] = useState(null); // {code, name}
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const startEdit = (s) => {
    setEditing({ oldCode: s.code, code: s.code, name: s.name || "" });
  };

  const cancelEdit = () => setEditing(null);

  const submit = async () => {
    if (editing) {
      const patch = {};
      if (editing.code && editing.code !== editing.oldCode) patch.code = editing.code;
      if ((editing.name || "") !== (sites.find((x) => x.code === editing.oldCode)?.name || "")) {
        patch.name = editing.name;
      }
      if (Object.keys(patch).length === 0) {
        setEditing(null);
        return;
      }
      setSaving(true);
      try {
        await updateSiteApi(editing.oldCode, patch);
        toast.success("Site mis à jour");
        setEditing(null);
        onRefresh && onRefresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!form.code.trim()) {
      toast.error("Code requis");
      return;
    }
    setSaving(true);
    try {
      await createSite({ code: form.code.trim().toUpperCase(), name: form.name.trim() });
      toast.success("Site ajouté");
      setForm({ code: "", name: "" });
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteSiteApi(confirmDelete.code);
      toast.success("Site supprimé");
      setConfirmDelete(null);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="mt-10" data-testid="sites-manager">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-tag">Gestion des sites / équipes</div>
          <h2
            className="text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1"
            style={{ fontFamily: "Chivo" }}
          >
            Sites
          </h2>
          <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">
            Le code du site (ex : DLP4) est utilisé dans les URL et les exports. Le nom est affiché à l&apos;écran.
          </p>
        </div>
      </div>

      <div className="border border-zinc-900 bg-zinc-100" data-testid="sites-list">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-900 text-white">
          <div className="col-span-3 label-tag !text-zinc-400">Code</div>
          <div className="col-span-6 label-tag !text-zinc-400">Nom</div>
          <div className="col-span-3 label-tag !text-zinc-400 text-right">Actions</div>
        </div>
        {sites.map((s) => {
          const isEditing = editing && editing.oldCode === s.code;
          return (
            <div
              key={s.code}
              className="grid grid-cols-12 gap-3 px-4 py-3 items-center bg-white border-t border-zinc-200"
              data-testid={`site-row-${s.code}`}
            >
              <div className="col-span-3">
                {isEditing ? (
                  <Input
                    value={editing.code}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                    className="rounded-none border-zinc-900 mono"
                    data-testid={`edit-site-code-${s.code}`}
                    maxLength={20}
                  />
                ) : (
                  <span className="mono font-bold">{s.code}</span>
                )}
              </div>
              <div className="col-span-6">
                {isEditing ? (
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="rounded-none border-zinc-900"
                    data-testid={`edit-site-name-${s.code}`}
                    placeholder="Ex : Perpignan"
                  />
                ) : (
                  <span>{s.name || <span className="text-zinc-400 italic">— sans nom —</span>}</span>
                )}
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={submit}
                      disabled={saving}
                      className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40"
                      data-testid={`save-site-${s.code}`}
                    >
                      {saving ? "…" : "Enregistrer"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(s)}
                      className="p-1.5 hover:bg-zinc-100"
                      title="Modifier"
                      data-testid={`edit-site-btn-${s.code}`}
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(s)}
                      className="p-1.5 hover:bg-red-100"
                      title="Supprimer"
                      data-testid={`delete-site-btn-${s.code}`}
                    >
                      <Trash size={14} weight="bold" className="text-red-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {/* New site row */}
        <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center bg-zinc-50 border-t border-zinc-200">
          <div className="col-span-3">
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="Code (DLP4…)"
              className="rounded-none border-zinc-900 mono"
              data-testid="new-site-code"
              maxLength={20}
            />
          </div>
          <div className="col-span-6">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nom (Perpignan…)"
              className="rounded-none border-zinc-900"
              data-testid="new-site-name"
            />
          </div>
          <div className="col-span-3 flex justify-end">
            <button
              onClick={submit}
              disabled={saving || !form.code.trim()}
              className="px-3 py-1 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="new-site-submit"
            >
              <Plus size={12} weight="bold" /> Ajouter
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-none border border-zinc-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce site ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le site {confirmDelete?.code} sera définitivement supprimé. Cette action est bloquée si des camions y sont encore rattachés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="rounded-none bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-site"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

